/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import {
    Configuration,
    ConfigurationParameters,
    DiskSpaceApi,
    DiskSpaceGetRequest,
    LastSelectedItemApi,
    LastSelectedItemPutRequest,
    MyShowsFilterList,
    MyShowsFiltersApi,
    MyShowsFiltersGetRequest,
} from 'client-code/myshows';

import { APPLICATION_FEATURE_AREA } from '../../config';
import { CommonHeaders } from '../CommonHeaders';
import { Constants } from '../constants';
import { IMyShowsServiceRepository } from '../interfaces';
import { Log } from '../logging/Log';
import { TMyShowsItemList, TServicesParams, TStorageInfo } from '../types';

export class MyShowsServiceRepositoryImpl implements IMyShowsServiceRepository {
    private static TAG: string = 'MyShowsServiceRepositoryImpl';

    private mServiceParams: TServicesParams;

    private mCommonHeaders: CommonHeaders;

    private mConfiguration: Configuration;

    public constructor(params: TServicesParams) {
        this.mServiceParams = params;

        // TODO: We need to differentiate the feature areas ie. GUIDE/MyShows
        this.mCommonHeaders = new CommonHeaders(APPLICATION_FEATURE_AREA.GUIDE);

        const ConfigParams: ConfigurationParameters = {
            basePath: this.mServiceParams.basePath,
            apiKey: this.mServiceParams.accessToken,
            baseOptions: {
                timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                headers: this.mCommonHeaders.headers,
            },
        };

        this.mConfiguration = new Configuration(ConfigParams);
    }

    private getCommonRequestParams() {
        return {
            applicationName: this.mCommonHeaders.headers.ApplicationName,
            applicationFeatureArea: this.mCommonHeaders.headers.ApplicationFeatureArea,
            applicationVersion: this.mCommonHeaders.headers.ApplicationVersion,
            deviceType: this.mCommonHeaders.headers.DeviceType,
            productName: this.mCommonHeaders.headers.ProductName,
            bodyId: this.mServiceParams.bodyId,
            whiny: this.mServiceParams.whiny || 'false',
        };
    }

    public async getDiskSpace(networkCallId?: number): Promise<TStorageInfo> {
        try {
            const diskSpaceApi = new DiskSpaceApi(this.mConfiguration);
            const requestParams: DiskSpaceGetRequest = this.getCommonRequestParams();

            const diskSpace = await diskSpaceApi.diskSpaceGet(requestParams);
            return { networkCallId, storageInfo: diskSpace.data };
        } catch (error: any) {
            error.networkCallId = networkCallId;
            Log.e(
                MyShowsServiceRepositoryImpl.TAG,
                'MyShowsServiceRepositoryImpl::getDiskSpace',
                error,
            );
            throw error;
        }
    }

    public async getMyShowsFilterList(): Promise<MyShowsFilterList> {
        try {
            const myShowsFilterApi = new MyShowsFiltersApi(this.mConfiguration);
            const requestParams: MyShowsFiltersGetRequest = {
                ...this.getCommonRequestParams(),
                acceptLanguage: this.mCommonHeaders.headers['Accept-Language'],
            };

            const myShowsFilters = await myShowsFilterApi.myShowsFiltersGet(requestParams);
            return myShowsFilters.data;
        } catch (error: any) {
            Log.e(
                MyShowsServiceRepositoryImpl.TAG,
                'MyShowsServiceRepositoryImpl::getMyShowsFilter',
                error,
            );
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async setLastSelectedItem(myShowsItemId: string, myShowsFilter?: string) {
        try {
            const lastSelectedItemApi = new LastSelectedItemApi(this.mConfiguration);
            const requestParams: LastSelectedItemPutRequest = {
                myShowsItemId,
                myShowsFilter,
                ...this.getCommonRequestParams(),
            };
            await lastSelectedItemApi.lastSelectedItemPut(requestParams);
        } catch (error: any) {
            Log.e(
                MyShowsServiceRepositoryImpl.TAG,
                'MyShowsServiceRepositoryImpl::setLastSelectedItem',
                error,
            );
            throw error;
        }
    }

    // eslint-disable-next-line class-methods-use-this
    public async getMyShowsItemList(): Promise<TMyShowsItemList> {
        throw new Error('Method not implemented.');
    }
}
